import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, CircleEllipsis, Mail, Plus, Search } from 'lucide-react';
import {
  apiCreateTeamMember,
  apiDeleteTeamMember,
  apiListMyProjects,
  apiListTeamMembers,
  apiUpdateTeamMember,
  mapTeamMemberDtoToUi,
} from '../api/client';
import { useWorkspaceAdds } from '../workspaceAddsContext';
import { sampleInvitePresets } from '../lib/sampleData';
import UserAvatar from '../components/UserAvatar';

const roleTranslationKeys = {
  Admin: 'roleAdmin',
  Manager: 'roleManager',
  Developer: 'roleDeveloper',
  Designer: 'roleDesigner',
  Viewer: 'roleViewer',
};

function roleLabel(role, t) {
  return t(roleTranslationKeys[role] || role);
}

function TeamMembersPage({ t, SkeletonCard, EmptyState }) {
  const workspaceAddsAllowed = useWorkspaceAdds();
  const [members, setMembers] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [menuMemberId, setMenuMemberId] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'Developer',
    projectIds: [],
  });
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [sampleDropOpen, setSampleDropOpen] = useState(false);
  const sampleDropRef = useRef(null);

  const roleOptions = ['Admin', 'Manager', 'Developer', 'Designer', 'Viewer'];
  const inviteRoleOptions = ['Manager', 'Developer', 'Designer', 'Viewer'];

  const refreshWorkspace = useCallback(async () => {
    setLoadError('');
    const [rows, projects] = await Promise.all([apiListTeamMembers(), apiListMyProjects()]);
    setMembers(rows.map(mapTeamMemberDtoToUi));
    setMyProjects(
      projects.map((p) => ({
        id: Number(p.id ?? p.Id),
        name: String(p.name ?? p.Name ?? ''),
      })),
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoadingMembers(true);
      setLoadError('');
      try {
        await refreshWorkspace();
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [refreshWorkspace]);

  const filteredMembers = members.filter((member) => {
    const matchesOnline = activeFilter === 'Online' ? member.online : true;
    const matchesRole = roleFilter === 'All' ? true : member.role === roleFilter;
    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      member.name.toLowerCase().includes(term) ||
      member.email.toLowerCase().includes(term) ||
      member.role.toLowerCase().includes(term);
    return matchesOnline && matchesRole && matchesSearch;
  });

  const toggleInviteProjectId = (id) => {
    setInviteForm((prev) => {
      const ids = new Set(prev.projectIds);
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      return { ...prev, projectIds: [...ids] };
    });
  };

  const cycleMemberRole = async (member) => {
    try {
      const idx = Math.max(0, roleOptions.indexOf(member.role));
      const next = roleOptions[(idx + 1) % roleOptions.length];
      await apiUpdateTeamMember(Number(member.id), { role: next });
      await refreshWorkspace();
      setSelectedMember((prev) => (prev && prev.id === member.id ? { ...prev, role: next } : prev));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t('couldNotUpdateRole'));
    }
    setMenuMemberId(null);
  };

  const removeMember = async (member) => {
    const ok = window.confirm(`${t('removeMemberConfirmPrefix')} ${member.name} (${member.email}) ${t('removeMemberConfirmSuffix')}`);
    if (!ok) return;
    try {
      await apiDeleteTeamMember(Number(member.id));
      await refreshWorkspace();
      if (selectedMember?.id === member.id) setSelectedMember(null);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t('couldNotDeleteMember'));
    }
    setMenuMemberId(null);
  };

  const sendInvite = async () => {
    setInviteError('');
    const name = inviteForm.name.trim();
    const email = inviteForm.email.trim();
    if (!name || !email) {
      setInviteError(t('inviteFullNameEmailRequired'));
      return;
    }
    try {
      await apiCreateTeamMember({
        name,
        email,
        role: inviteForm.role,
        online: false,
        projectIds: inviteForm.projectIds,
      });
      setInviteForm({ name: '', email: '', role: 'Developer', projectIds: [] });
      setShowInviteModal(false);
      await refreshWorkspace();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : t('inviteFailed'));
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">{t('teamMembersTitle')}</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
            {workspaceAddsAllowed
              ? t('teamManageSubtitle')
              : t('teamReadonlySubtitle')}
          </p>
        </div>
        {workspaceAddsAllowed && (
          <button
            type="button"
            onClick={() => {
              setInviteError('');
              setInviteForm({
                name: '',
                email: '',
                role: 'Developer',
                projectIds: [],
              });
              setShowInviteModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand transition hover:opacity-95"
          >
            <Plus size={16} />
            {t('inviteMember')}
          </button>
        )}
      </div>

      {loadError && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
          {loadError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
        {['All', 'Online'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveFilter(tab)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              activeFilter === tab
                ? 'bg-brand-500 text-white'
                : 'border border-brand-200 text-slate-600 dark:border-brand-900 dark:text-slate-300'
            }`}
          >
            {tab === 'All' ? t('filterAll') : t('online')}
          </button>
        ))}
        <select
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value)}
          className="rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-950"
        >
          <option value="All">{t('byRole')}</option>
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {roleLabel(role, t)}
            </option>
          ))}
        </select>
        <div className="relative ml-auto min-w-56">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('searchMembers')}
            className="w-full rounded-lg border border-brand-200 bg-white py-1.5 pl-8 pr-2.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {loadingMembers ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : filteredMembers.length === 0 ? (
          <div className="sm:col-span-2 xl:col-span-3">
            <EmptyState
              icon="👥"
              title={t('noMembersFound')}
              description={
                loadError
                  ? t('teamEmptyFixIssue')
                  : workspaceAddsAllowed
                    ? t('teamEmptyInvitePeople')
                    : t('teamEmptyNoSearch')
              }
              ctaLabel={workspaceAddsAllowed ? `+ ${t('inviteMember')}` : undefined}
              onCta={workspaceAddsAllowed ? () => setShowInviteModal(true) : undefined}
            />
          </div>
        ) : (
          filteredMembers.map((member) => (
            <article
              key={member.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedMember(member)}
                  className="flex items-center gap-2 text-left"
                >
                  <div className="relative">
                    <UserAvatar
                      name={member.name}
                      email={member.email}
                      imageUrl={member.avatarUrl ?? member.avatar ?? null}
                      sizeClass="h-10 w-10"
                    />
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white dark:ring-slate-900 ${member.online ? 'bg-emerald-500' : 'bg-slate-400'}`}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">{member.name}</p>
                    <span className="mt-0.5 inline-block rounded-full bg-brand-100 px-2 py-0.5 text-[11px] text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                      {roleLabel(member.role, t)}
                    </span>
                  </div>
                </button>
                {workspaceAddsAllowed ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setMenuMemberId((prev) => (prev === member.id ? null : member.id))}
                      className="rounded-md p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
                    >
                      <CircleEllipsis size={14} />
                    </button>
                    {menuMemberId === member.id && (
                      <div className="absolute right-0 z-10 mt-1 w-44 rounded-lg border border-brand-200 bg-white p-1 text-xs shadow-lg dark:border-brand-900 dark:bg-slate-900">
                        <button
                          type="button"
                          onClick={() => cycleMemberRole(member)}
                          className="block w-full rounded px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                        >
                          {t('changeRole')}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeMember(member)}
                          className="block w-full rounded px-2 py-1.5 text-left hover:bg-red-50 dark:hover:bg-red-900/25"
                        >
                          {t('removeMember')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMember(member);
                            setMenuMemberId(null);
                          }}
                          className="block w-full rounded px-2 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-900/20"
                        >
                          {t('viewProfile')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="mt-3 space-y-1 text-xs text-slate-500 dark:text-slate-300">
                <p className="inline-flex items-center gap-1">
                  <Mail size={12} />
                  {member.email}
                </p>
                <p>{t('activeProjects')}: {member.activeProjects}</p>
                <p>{t('tasks')}: {member.taskCount}</p>
              </div>
            </article>
          ))
        )}
      </div>

      {showInviteModal && workspaceAddsAllowed && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('inviteMember')}</h3>
            <div className="mt-3 space-y-3 text-xs">
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('fullName')}</span>
                <input
                  value={inviteForm.name}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('email')}</span>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
                  className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('role')}</span>
                <select
                  value={inviteForm.role}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, role: event.target.value }))}
                  className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-950"
                >
                  {inviteRoleOptions.map((role) => (
                    <option key={role} value={role}>
                      {roleLabel(role, t)}
                    </option>
                  ))}
                </select>
              </label>
              <div>
                <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('projectAccess')}</span>
                <div className="max-h-28 space-y-1 overflow-y-auto rounded-lg border border-brand-200 p-2 dark:border-brand-900">
                  {myProjects.length === 0 ? (
                    <p className="text-slate-500">{t('createProjectFirstAccess')}</p>
                  ) : (
                    myProjects.map((proj) => (
                      <label key={proj.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={inviteForm.projectIds.includes(Number(proj.id))}
                          onChange={() => toggleInviteProjectId(Number(proj.id))}
                        />
                        <span>{proj.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>

            {inviteError && (
              <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800 dark:border-red-900 dark:bg-red-900/25 dark:text-red-200">
                {inviteError}
              </p>
            )}

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              {/* Fill sample data dropdown */}
              <div className="relative me-auto" ref={sampleDropRef}>
                <button
                  type="button"
                  onClick={() => setSampleDropOpen((v) => !v)}
                  className="flex items-center gap-1 rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
                >
                  {t('fillSampleData')}
                  <ChevronDown size={12} className={sampleDropOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
                </button>
                {sampleDropOpen && (
                  <div className="absolute bottom-full mb-2 start-0 z-20 w-64 overflow-hidden rounded-xl border border-brand-200 bg-white shadow-xl dark:border-brand-900 dark:bg-slate-900">
                    {sampleInvitePresets(myProjects).map((s, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setInviteForm({ name: s.name, email: s.email, role: s.role, projectIds: s.projectIds });
                          setInviteError('');
                          setSampleDropOpen(false);
                        }}
                        className="w-full border-b border-brand-100 px-3 py-2.5 text-start last:border-0 hover:bg-brand-50 dark:border-brand-800 dark:hover:bg-brand-900/40"
                      >
                        <p className="text-xs font-semibold text-slate-800 dark:text-white truncate">{s.name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{s.role} · {s.email.split('@')[0]}@…</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={() => sendInvite()}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t('saveMember')}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedMember && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <UserAvatar
                  name={selectedMember.name}
                  email={selectedMember.email}
                  imageUrl={selectedMember.avatarUrl ?? selectedMember.avatar ?? null}
                  sizeClass="h-12 w-12"
                />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{selectedMember.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    {roleLabel(selectedMember.role, t)} • {selectedMember.email}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">
                    {t('joined')}: {selectedMember.joinedDate || '—'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMember(null)}
                className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-brand-50 dark:text-slate-300 dark:hover:bg-brand-900/20"
              >
                {t('close')}
              </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-brand-200 p-3 dark:border-brand-900">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('assignedTasks')}</p>
                <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                  {(selectedMember.assignedTasks || []).length === 0 ? (
                    <li>{t('noTasksAssignedYet')}</li>
                  ) : (
                    selectedMember.assignedTasks.map((taskName) => (
                      <li key={taskName}>• {taskName}</li>
                    ))
                  )}
                </ul>
              </div>
              <div className="rounded-lg border border-brand-200 p-3 dark:border-brand-900">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('activityLast5Buckets')}</p>
                <div className="mt-3 flex h-24 items-end gap-2">
                  {(selectedMember.activity || [0, 0, 0, 0, 0]).map((value, index) => (
                    <div key={`${selectedMember.id}-bar-${index}`} className="flex-1">
                      <div
                        className="w-full rounded-t bg-brand-500"
                        style={{ height: `${Math.max(10, Number(value || 0) * 12)}px` }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {workspaceAddsAllowed && (
              <div className="mt-4 flex flex-wrap gap-2">
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={selectedMember.online}
                    onChange={(e) =>
                      apiUpdateTeamMember(Number(selectedMember.id), { online: e.target.checked })
                        .then(() => refreshWorkspace())
                        .then(() =>
                          setSelectedMember((prev) =>
                            prev && prev.id === selectedMember.id
                              ? { ...prev, online: e.target.checked }
                              : prev,
                          ),
                        )
                        .catch((err) => window.alert(err?.message ?? t('updateFailed')))
                    }
                  />
                  {t('online')}
                </label>
                <button
                  type="button"
                  className="text-xs font-medium text-red-700 hover:underline dark:text-red-400"
                  onClick={() => {
                    if (
                      !window.confirm(
                        `${t('removeMemberConfirmPrefix')} ${selectedMember.name} (${selectedMember.email}) ${t('removeMemberConfirmSuffix')}`,
                      )
                    )
                      return;
                    apiDeleteTeamMember(Number(selectedMember.id))
                      .then(() => {
                        setSelectedMember(null);
                        return refreshWorkspace();
                      })
                      .catch((err) => window.alert(err?.message ?? t('couldNotDelete')));
                  }}
                >
                  {t('removeFromRoster')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default TeamMembersPage;
